import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Activity, 
  Search, 
  Filter, 
  Calendar,
  User,
  FileText,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ActivityLog {
  id: string;
  user_email: string;
  user_name: string;
  user_type: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  entity_name: string | null;
  changes_summary: string | null;
  created_at: string;
  action_label: string;
  entity_label: string;
}

export default function ActivityLogsPage() {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState<string>('all');
  const [filterEntity, setFilterEntity] = useState<string>('all');
  const [filterUser, setFilterUser] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Buscar logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('activity_logs_view')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      console.log(`Logs carregados: ${data?.length || 0} registros`);
      console.log('Dados dos logs:', data);
      setLogs(data || []);
      setFilteredLogs(data || []);
      setLastUpdate(new Date());
      toast.success(`${data?.length || 0} registros carregados`);
    } catch (error: any) {
      console.error('Erro ao buscar logs:', error);
      toast.error('Erro ao carregar logs de atividades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();

    // Atualizar a cada 10 segundos
    const interval = setInterval(fetchLogs, 10000);

    // Adicionar listener de realtime
    const channel = supabase
      .channel('activity-logs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'activity_logs',
        },
        (payload) => {
          console.log('Nova atividade detectada:', payload);
          fetchLogs(); // Recarregar logs quando houver mudança
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, []);

  // Aplicar filtros
  useEffect(() => {
    let filtered = logs;
    console.log('Aplicando filtros. Total de logs:', logs.length);

    // Filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.changes_summary?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log('Após filtro de busca:', filtered.length);
    }

    // Filtro de ação
    if (filterAction !== 'all') {
      filtered = filtered.filter(log => log.action === filterAction);
      console.log('Após filtro de ação:', filtered.length);
    }

    // Filtro de entidade
    if (filterEntity !== 'all') {
      filtered = filtered.filter(log => log.entity_type === filterEntity);
      console.log('Após filtro de entidade:', filtered.length);
    }

    // Filtro de usuário
    if (filterUser !== 'all') {
      filtered = filtered.filter(log => log.user_email === filterUser);
      console.log('Após filtro de usuário:', filtered.length);
    }

    // Filtro de data inicial
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      console.log('Filtrando por data inicial:', start);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.created_at);
        console.log('Log date:', logDate, 'Start:', start, 'Passa?', logDate >= start);
        return logDate >= start;
      });
      console.log('Após filtro de data inicial:', filtered.length);
    }

    // Filtro de data final
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      console.log('Filtrando por data final:', end);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.created_at);
        return logDate <= end;
      });
      console.log('Após filtro de data final:', filtered.length);
    }

    console.log('Total de logs filtrados:', filtered.length);
    setFilteredLogs(filtered);
  }, [searchTerm, filterAction, filterEntity, filterUser, startDate, endDate, logs]);

  // Obter lista de usuários únicos
  const uniqueUsers = Array.from(new Set(logs.map(log => log.user_email)));

  // Obter ícone da ação
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create':
        return '➕';
      case 'update':
        return '✏️';
      case 'delete':
        return '🗑️';
      case 'login':
        return '🔐';
      case 'logout':
        return '🚪';
      default:
        return '📝';
    }
  };

  // Obter cor da ação
  const getActionColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'text-green-600 bg-green-50';
      case 'update':
        return 'text-blue-600 bg-blue-50';
      case 'delete':
        return 'text-red-600 bg-red-50';
      case 'login':
        return 'text-purple-600 bg-purple-50';
      case 'logout':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // Limpar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilterAction('all');
    setFilterEntity('all');
    setFilterUser('all');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-8 h-8 text-blue-600" />
            Registro de Atividades
          </h1>
          <p className="text-gray-600 mt-1">
            Histórico completo de todas as ações realizadas no sistema
            {lastUpdate && (
              <span className="text-sm text-gray-500 ml-2">
                • Última atualização: {format(lastUpdate, "HH:mm:ss", { locale: ptBR })}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => console.log('Logs:', logs, 'Filtrados:', filteredLogs)} 
            variant="outline" 
            size="sm"
          >
            Debug Console
          </Button>
          <Button onClick={fetchLogs} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Busca */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Filtro de Ação */}
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as ações" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as ações</SelectItem>
                  <SelectItem value="create">➕ Criação</SelectItem>
                  <SelectItem value="update">✏️ Edição</SelectItem>
                  <SelectItem value="delete">🗑️ Exclusão</SelectItem>
                  <SelectItem value="login">🔐 Login</SelectItem>
                  <SelectItem value="logout">🚪 Logout</SelectItem>
                </SelectContent>
              </Select>

              {/* Filtro de Entidade */}
              <Select value={filterEntity} onValueChange={setFilterEntity}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as entidades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as entidades</SelectItem>
                  <SelectItem value="product">📦 Produtos</SelectItem>
                  <SelectItem value="category">📁 Categorias</SelectItem>
                  <SelectItem value="order">🛒 Pedidos</SelectItem>
                  <SelectItem value="customer">👤 Clientes</SelectItem>
                  <SelectItem value="admin">👨‍💼 Admins</SelectItem>
                </SelectContent>
              </Select>

              {/* Filtro de Usuário */}
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os usuários" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os usuários</SelectItem>
                  {uniqueUsers.map(user => (
                    <SelectItem key={user} value={user}>
                      {user}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtros de Data */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Data Inicial
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Data Final
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Atalhos de Período */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  // Ajustar para o timezone local
                  const year = today.getFullYear();
                  const month = String(today.getMonth() + 1).padStart(2, '0');
                  const day = String(today.getDate()).padStart(2, '0');
                  const todayStr = `${year}-${month}-${day}`;
                  console.log('Hoje (data local):', todayStr, today);
                  setStartDate(todayStr);
                  setEndDate(todayStr);
                }}
              >
                Hoje
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const yesterday = new Date(today);
                  yesterday.setDate(yesterday.getDate() - 1);
                  const dateStr = yesterday.toISOString().split('T')[0];
                  setStartDate(dateStr);
                  setEndDate(dateStr);
                }}
              >
                Ontem
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const weekAgo = new Date(today);
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  setStartDate(weekAgo.toISOString().split('T')[0]);
                  setEndDate(today.toISOString().split('T')[0]);
                }}
              >
                Últimos 7 dias
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const monthAgo = new Date(today);
                  monthAgo.setDate(monthAgo.getDate() - 30);
                  setStartDate(monthAgo.toISOString().split('T')[0]);
                  setEndDate(today.toISOString().split('T')[0]);
                }}
              >
                Últimos 30 dias
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Todos os períodos
              </Button>
            </div>

            {/* Botão Limpar Filtros */}
            {(searchTerm || filterAction !== 'all' || filterEntity !== 'all' || filterUser !== 'all' || startDate || endDate) && (
              <div className="mt-4">
                <Button onClick={clearFilters} variant="outline" size="sm">
                  Limpar Filtros
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Total de Registros</p>
              <p className="text-3xl font-bold text-blue-600">{logs.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Filtrados</p>
              <p className="text-3xl font-bold text-green-600">{filteredLogs.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Usuários Ativos</p>
              <p className="text-3xl font-bold text-purple-600">{uniqueUsers.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Hoje</p>
              <p className="text-3xl font-bold text-orange-600">
                {logs.filter(log => {
                  const logDate = new Date(log.created_at);
                  const today = new Date();
                  return logDate.toDateString() === today.toDateString();
                }).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Histórico de Atividades
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-600 mt-4">Carregando logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto text-gray-400" />
              <p className="text-gray-600 mt-4 font-semibold">Nenhum registro encontrado</p>
              {logs.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md mx-auto">
                  <p className="text-sm text-yellow-800">
                    <strong>Atenção:</strong> Existem {logs.length} registro(s) no total, mas os filtros estão bloqueando a exibição.
                  </p>
                  <div className="mt-2 text-xs text-yellow-700 text-left">
                    <p>Filtros ativos:</p>
                    <ul className="list-disc list-inside mt-1">
                      {searchTerm && <li>Busca: "{searchTerm}"</li>}
                      {filterAction !== 'all' && <li>Ação: {filterAction}</li>}
                      {filterEntity !== 'all' && <li>Entidade: {filterEntity}</li>}
                      {filterUser !== 'all' && <li>Usuário: {filterUser}</li>}
                      {startDate && <li>Data inicial: {startDate}</li>}
                      {endDate && <li>Data final: {endDate}</li>}
                    </ul>
                  </div>
                  <div className="mt-3 text-xs text-yellow-700">
                    <p>💡 Dica: Os logs existentes podem ser de outras datas. Tente:</p>
                    <ul className="list-disc list-inside mt-1">
                      <li>Clicar em "Todos os períodos" para ver todos os logs</li>
                      <li>Ajustar as datas manualmente</li>
                      <li>Usar "Últimos 7 dias" ou "Últimos 30 dias"</li>
                    </ul>
                  </div>
                  <Button 
                    onClick={clearFilters} 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                  >
                    Limpar Todos os Filtros
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {console.log('Renderizando logs. Total:', filteredLogs.length, 'Logs:', filteredLogs)}
              {filteredLogs.map((log, index) => {
                console.log(`Renderizando log ${index}:`, log);
                // Debug: verificar se o log tem todos os campos
                if (!log.user_name || !log.action_label || !log.entity_label) {
                  console.warn('Log com campos faltando:', log);
                  return (
                    <div key={log.id} className="p-4 border border-red-300 rounded-lg bg-red-50">
                      <p className="text-red-600 font-semibold">Log com dados incompletos (ID: {log.id})</p>
                      <pre className="text-xs mt-2">{JSON.stringify(log, null, 2)}</pre>
                    </div>
                  );
                }
                
                return (
                  <div
                    key={log.id}
                    className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                  {/* Ícone da Ação */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl ${getActionColor(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* Usuário e Ação */}
                        <p className="text-sm font-medium text-gray-900">
                          <span className="font-semibold">{log.user_name}</span>
                          {' '}
                          <span className="text-gray-600">{log.action_label.toLowerCase()}</span>
                          {' '}
                          <span className="font-semibold">{log.entity_label}</span>
                          {log.entity_name && (
                            <>
                              {': '}
                              <span className="text-blue-600">{log.entity_name}</span>
                            </>
                          )}
                        </p>

                        {/* Resumo das Mudanças */}
                        {log.changes_summary && (
                          <p className="text-sm text-gray-600 mt-1">
                            {log.changes_summary}
                          </p>
                        )}

                        {/* Metadados */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.user_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(log.created_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
